import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import { Button as TamaguiButton, XStack, YStack } from "tamagui";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { trackKindredProductEvent } from "@/lib/productAnalytics";
import { HELPER_PACKS, SUPPORT_CATEGORIES, SUPPORT_CATEGORY_GUIDES } from "@/lib/support-data";
import {
  buildSupportCardLinks,
  buildUsSupportRoutingSummary,
  getStaticSupportCategoryCards,
  getSupportCountryResourceBundle,
  hasLiveSupportForCountry,
  isSupportPlaybookAvailableForCountry,
  isSupportPausedCountry,
  matchesSupportCardCountry,
  matchesSupportCategory,
  SUPPORT_CANONICAL_SECTION,
  SUPPORT_LEGACY_SECTION,
  type UsSupportRoutingMode,
} from "../../shared/support";
import { getMemberCountryLabel } from "../../shared/location";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type SupportCard = {
  id: string;
  section: string;
  category: string;
  country_scope?: string | null;
  title: string;
  body: string;
  link_label?: string | null;
  link_url?: string | null;
  routing_mode?: UsSupportRoutingMode | null;
  rules_vary_by_state?: boolean | null;
  sort_order: number;
  last_reviewed_at?: string | null;
};

type MemberSupportProfile = {
  country_code: string | null;
  postcode: string | null;
  support_us_state: string | null;
  support_us_county_or_city: string | null;
  support_us_school_district: string | null;
  support_us_school_zip: string | null;
  support_us_tribal_or_territory: string | null;
};

const SupportCategory = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cards, setCards] = useState<SupportCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [memberSupportProfile, setMemberSupportProfile] = useState<MemberSupportProfile | null>(null);

  const category = useMemo(
    () => SUPPORT_CATEGORIES.find((item) => item.slug === slug),
    [slug],
  );
  const guide = slug ? SUPPORT_CATEGORY_GUIDES[slug] : undefined;
  const memberCountryCode = memberSupportProfile?.country_code ?? null;
  const memberPostcode = memberSupportProfile?.postcode ?? null;
  const suggestedPacks = useMemo(
    () =>
      (guide?.packSlugs ?? [])
        .map((packSlug) => HELPER_PACKS.find((pack) => pack.slug === packSlug))
        .filter((pack): pack is (typeof HELPER_PACKS)[number] => Boolean(pack)),
    [guide],
  );
  const liveSupportAvailable = hasLiveSupportForCountry(memberCountryCode);
  const supportPaused = isSupportPausedCountry(memberCountryCode);
  const supportCountryLabel = memberCountryCode ? getMemberCountryLabel(memberCountryCode) : "your country";
  const supportResourceBundle = getSupportCountryResourceBundle(memberCountryCode);
  const visibleSuggestedPacks = liveSupportAvailable
    ? suggestedPacks.filter((pack) => isSupportPlaybookAvailableForCountry(pack.slug, memberCountryCode))
    : [];

  useEffect(() => {
    const fetchCards = async () => {
      if (!slug) {
        setCards([]);
        setLoading(false);
        return;
      }

      const [{ data }, { data: profile }] = await Promise.all([
        supabase
          .from("content_cards")
          .select(
            "id, section, category, country_scope, title, body, link_label, link_url, routing_mode, rules_vary_by_state, sort_order, last_reviewed_at",
          )
          .in("section", [SUPPORT_LEGACY_SECTION, SUPPORT_CANONICAL_SECTION])
          .eq("is_active", true)
          .order("sort_order", { ascending: true }),
        supabase
          .from("my_private_profile")
          .select("country_code, postcode, support_us_state, support_us_county_or_city, support_us_school_district, support_us_school_zip, support_us_tribal_or_territory")
          .maybeSingle(),
      ]);

      setMemberSupportProfile((profile as MemberSupportProfile | null) ?? null);
      const staticCards = getStaticSupportCategoryCards(
        slug,
        profile?.country_code ?? null,
      );
      const nextCards = staticCards.length
        ? (staticCards as SupportCard[])
        : hasLiveSupportForCountry(profile?.country_code ?? null)
          ? ((data ?? []) as SupportCard[])
              .filter(
                (card) =>
                  matchesSupportCategory(card, slug) &&
                  matchesSupportCardCountry(card, profile?.country_code ?? null),
              )
              .sort((left, right) => left.sort_order - right.sort_order)
          : [];

      setCards(nextCards);
      setLoading(false);
    };

    void fetchCards();
  }, [slug]);

  if (loading) {
    return (
      <AppLoadingState
        title="Loading support area"
        body="Pulling together the right guidance, helper packs, and notes for this topic."
      />
    );
  }

  return (
    <YStack gap="$md" pt="$xs" pb="$md">
      <AppButton type="button" tone="ghost" size="sm" onPress={() => navigate("/support")} alignSelf="flex-start">
        Back to Support
      </AppButton>

      <AppCard backgroundColor="$panelBackground">
        <AppCardBody gap="$sm">
          <AppText color="$primary" fontSize="$xs" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold} textTransform="uppercase" letterSpacing={1.2}>
            Support area
          </AppText>
          <XStack alignItems="flex-start" gap="$sm">
            <AppText fontSize="$hero" lineHeight="$hero">
              {category?.emoji}
            </AppText>
            <YStack flex={1} minWidth={0} gap="$xs">
              <AppText fontFamily={kindredFontFamilies.display} fontSize="$hero" lineHeight="$hero" color="$heading">
                {category?.name}
              </AppText>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                {guide?.intro ?? category?.description}
              </AppText>
            </YStack>
          </XStack>
        </AppCardBody>
      </AppCard>

      {!liveSupportAvailable ? (
        <YStack gap="$sm">
          <AppEmptyState
            title={
              supportPaused
                ? `Support in ${supportCountryLabel} is paused`
                : `Support for ${supportCountryLabel} is coming soon`
            }
            body={
              supportPaused
                ? `${supportCountryLabel} is not on an active Kindred rollout right now. We have paused ${supportCountryLabel} while we put the right public, legal, and support layer in place. We will not show another country's rules as if they apply where you are.`
                : `Kindred Community is ready in ${supportCountryLabel}, and local support guidance for this area is still being built. Start with the local help below while we finish the fuller guidance. We will not show another country's rules as if they apply where you are.`
            }
          />

          <AppCard backgroundColor="$panelSoftBackground" borderColor="$borderStrong">
            <AppCardBody gap="$sm">
              <YStack gap={4}>
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                  Getting help in {supportCountryLabel}
                </AppText>
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                  {supportPaused
                    ? `${supportCountryLabel} is paused for now, so these are safer national starting points while Kindred holds back a wider launch until the local legal and support layer is properly ready.`
                    : "Full local Kindred guidance is still coming. These are safer local starting points than relying on UK-only support routes."}
                </AppText>
              </YStack>
              <XStack flexWrap="wrap" gap="$xs">
                {supportResourceBundle.starterLinks.map((link) => (
                  <a key={link.href} href={link.href} style={{ textDecoration: "none" }}>
                    <XStack
                      borderWidth={1}
                      borderColor="$borderStrong"
                      borderRadius="$pill"
                      backgroundColor="$background"
                      paddingHorizontal={12}
                      paddingVertical={8}
                    >
                      <AppText color="$primary" fontSize="$sm" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                        {link.label}
                      </AppText>
                    </XStack>
                  </a>
                ))}
              </XStack>
            </AppCardBody>
          </AppCard>

          <AppCard backgroundColor="$accentSoft" borderColor="$accent">
            <AppCardBody gap="$sm">
              <YStack gap={4}>
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                  Need support right now?
                </AppText>
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                  If today feels unsafe, overwhelming, or too much to hold, start here first.
                </AppText>
              </YStack>
              <XStack flexWrap="wrap" gap="$xs">
                {supportResourceBundle.urgentLinks.map((link) => (
                  <a key={link.href} href={link.href} style={{ textDecoration: "none" }}>
                    <XStack
                      borderWidth={1}
                      borderColor="$accent"
                      borderRadius="$pill"
                      backgroundColor="$background"
                      paddingHorizontal={12}
                      paddingVertical={8}
                    >
                      <AppText color="$secondary" fontSize="$sm" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                        {link.label}
                      </AppText>
                    </XStack>
                  </a>
                ))}
              </XStack>
            </AppCardBody>
          </AppCard>
        </YStack>
      ) : null}

      {visibleSuggestedPacks.length > 0 ? (
        <YStack gap="$sm">
          <AppSectionHeader
            title="Helper packs for this area"
            subtitle="The most relevant step-by-step guides for this topic."
          />
          <div className="grid gap-3 sm:grid-cols-2">
            {visibleSuggestedPacks.map((pack) => (
                <TamaguiButton
                  key={pack.slug}
                  unstyled
                  type="button"
                  onPress={async () => {
                    await trackKindredProductEvent({
                      userId: user?.id,
                      eventName: "support_card_opened",
                      screenName: "support_category",
                      entityType: "helper_pack",
                      entityId: pack.slug,
                    });
                    navigate(`/support/pack/${pack.slug}`);
                  }}
                  width="100%"
                alignItems="stretch"
                justifyContent="stretch"
                pressStyle={{ opacity: 0.94 }}
              >
                <AppCard backgroundColor="$primary" borderColor="$primary" height="100%">
                  <AppCardBody>
                    <XStack alignItems="flex-start" justifyContent="space-between" gap="$sm">
                      <YStack flex={1} minWidth={0} gap="$sm">
                        <XStack alignItems="flex-start" gap="$sm">
                          <AppText fontSize={24} lineHeight={28}>
                            {pack.emoji}
                          </AppText>
                          <AppText
                            flex={1}
                            fontFamily={kindredFontFamilies.sansBold}
                            fontSize="$sm"
                            lineHeight="$sm"
                            color="$primaryForeground"
                          >
                            {pack.title}
                          </AppText>
                        </XStack>
                        <AppText color="rgba(251, 248, 242, 0.86)" fontSize="$sm" lineHeight="$md">
                          {pack.description}
                        </AppText>
                      </YStack>
                      <ArrowRight size={16} className="mt-1 shrink-0 text-white" />
                    </XStack>
                  </AppCardBody>
                </AppCard>
              </TamaguiButton>
            ))}
          </div>
        </YStack>
      ) : null}

      {cards.length > 0 ? (
        <YStack gap="$sm">
          <AppSectionHeader
            title="Useful notes and onward links"
            subtitle="Short answers and signposts collected for this area."
          />

          {cards.map((card) => {
            const isExpanded = expanded === card.id;
            const links = buildSupportCardLinks(card, {
              countryCode: memberCountryCode,
              postcode: memberPostcode,
            });
            const routingSummary = buildUsSupportRoutingSummary(card, {
              countryCode: memberCountryCode,
              postcode: memberPostcode,
              support_us_state: memberSupportProfile?.support_us_state ?? null,
              support_us_county_or_city: memberSupportProfile?.support_us_county_or_city ?? null,
              support_us_school_district: memberSupportProfile?.support_us_school_district ?? null,
              support_us_school_zip: memberSupportProfile?.support_us_school_zip ?? null,
              support_us_tribal_or_territory: memberSupportProfile?.support_us_tribal_or_territory ?? null,
            });
            const visibleLinks = routingSummary?.actionUrl
              ? links.filter((link) => link.url !== routingSummary.actionUrl)
              : links;

            return (
              <AppCard key={card.id}>
                <TamaguiButton
                  unstyled
                  type="button"
                  width="100%"
                  onPress={() => setExpanded(isExpanded ? null : card.id)}
                  pressStyle={{ opacity: 0.94 }}
                >
                  <AppCardBody>
                    <XStack alignItems="center" justifyContent="space-between" gap="$sm">
                      <AppText flex={1} paddingRight="$sm" fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                        {card.title}
                      </AppText>
                      {isExpanded ? (
                        <ChevronDown size={18} className="shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronRight size={18} className="shrink-0 text-muted-foreground" />
                      )}
                    </XStack>
                  </AppCardBody>
                </TamaguiButton>

                {isExpanded ? (
                  <AppCardBody pt={0} gap="$xs">
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" whiteSpace="pre-wrap">
                      {card.body}
                    </AppText>
                    {routingSummary ? (
                      <AppCard backgroundColor="$panelSoftBackground" borderColor="$borderStrong">
                        <AppCardBody gap="$sm">
                          <YStack gap={4}>
                            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                              {routingSummary.title}
                            </AppText>
                            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                              {routingSummary.body}
                            </AppText>
                            {routingSummary.missingFieldLabels.length ? (
                              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                                Add {routingSummary.missingFieldLabels.join(" and ")} in Settings if you want Kindred to route this more precisely.
                              </AppText>
                            ) : null}
                          </YStack>
                          <XStack flexWrap="wrap" gap="$xs">
                            {routingSummary.actionUrl ? (
                              <AppButton
                                size="sm"
                                onPress={() => {
                                  void trackKindredProductEvent({
                                    userId: user?.id,
                                    eventName: "support_card_opened",
                                    screenName: "support_category",
                                    entityType: "support_card",
                                    entityId: card.id,
                                    metadata: { destinationUrl: routingSummary.actionUrl, destinationLabel: routingSummary.actionLabel },
                                  });
                                  window.open(routingSummary.actionUrl, "_blank", "noopener,noreferrer");
                                }}
                              >
                                {routingSummary.actionLabel}
                              </AppButton>
                            ) : null}
                            {routingSummary.missingFieldLabels.length ? (
                              <AppButton tone="outline" size="sm" onPress={() => navigate("/settings")}>
                                Add routing details in Settings
                              </AppButton>
                            ) : null}
                          </XStack>
                        </AppCardBody>
                      </AppCard>
                    ) : null}
                    {visibleLinks.length ? (
                      <YStack gap="$xs">
                        {visibleLinks.map((link) => (
                          <a
                            key={`${card.id}-${link.label}`}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="kindred-inline-link"
                            onClick={() => {
                              void trackKindredProductEvent({
                                userId: user?.id,
                                eventName: "support_card_opened",
                                screenName: "support_category",
                                entityType: "support_card",
                                entityId: card.id,
                                metadata: { destinationUrl: link.url, destinationLabel: link.label },
                              });
                            }}
                          >
                            {link.label}
                          </a>
                        ))}
                      </YStack>
                    ) : null}
                    {card.last_reviewed_at ? (
                      <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                        Last reviewed: {format(new Date(card.last_reviewed_at), "d MMMM yyyy")}
                      </AppText>
                    ) : null}
                  </AppCardBody>
                ) : null}
              </AppCard>
            );
          })}
        </YStack>
      ) : null}

      {cards.length === 0 && suggestedPacks.length === 0 ? (
        <AppEmptyState
          title="This support area is being filled out"
          body="There is nothing live in this section yet, but the structure is ready for new guidance."
        />
      ) : null}
    </YStack>
  );
};

export default SupportCategory;
